import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Search, X } from 'lucide-react';
import { useState } from 'react';
import { findPrompts, PROMPT_CATEGORIES, PROMPTS, type PromptCategory } from '../model/prompts';
import { PromptFilters } from './PromptFilters';
import { PromptGroup } from './PromptGroup';

type Props = {
  open: boolean;
  /** The category to open on, from "See all prompts" under a home tab; null shows all. */
  initialCategory: PromptCategory | null;
  onClose: () => void;
  onPick: (prompt: string) => void;
};

/** Every prompt, searchable and filtered by category. Picking one puts it in the question box and closes the drawer. */
export function PromptLibrary({ open, initialCategory, onClose, onPick }: Props) {
  return (
    <Drawer anchor="right" open={open} onClose={onClose} sx={{ '& .MuiDrawer-paper': { width: { xs: '100%', sm: 380 }, borderLeft: 0 } }}>
      {/* Mounted per opening, so the search and filter start fresh and follow the tab it was opened from. */}
      {open ? <LibraryBody initialCategory={initialCategory} onClose={onClose} onPick={(p) => { onPick(p); onClose(); }} /> : null}
    </Drawer>
  );
}

function LibraryBody({ initialCategory, onClose, onPick }: Omit<Props, 'open'>) {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<PromptCategory | null>(initialCategory);
  const found = findPrompts(search, category);
  const groups = PROMPT_CATEGORIES.map((c) => ({ category: c, prompts: found.filter((p) => p.category === c) })).filter((g) => g.prompts.length > 0);

  return (
    <Stack component="section" aria-labelledby="prompt-library-title" sx={{ height: '100%' }}>
      <Stack sx={{ px: 2.5, pt: 2.5, pb: 2, gap: 1.75, borderBottom: 1, borderColor: 'divider' }}>
        <Stack direction="row" sx={{ alignItems: 'flex-start' }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography id="prompt-library-title" variant="h3" component="h2">Prompt library</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>{PROMPTS.length} prompts for OCBC research</Typography>
          </Box>
          <IconButton size="small" aria-label="Close prompt library" onClick={onClose} sx={{ mr: -0.5, color: 'text.secondary' }}>
            <X size={18} />
          </IconButton>
        </Stack>
        <TextField
          fullWidth
          type="search"
          placeholder="Search prompts"
          value={search}
          onChange={(e) => { setSearch(e.target.value); }}
          slotProps={{
            htmlInput: { 'aria-label': 'Search prompts' },
            input: { startAdornment: <InputAdornment position="start"><Search size={16} aria-hidden /></InputAdornment> },
          }}
        />
        <PromptFilters value={category} onChange={setCategory} />
      </Stack>
      <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 1.25, py: 1.5 }} aria-live="polite">
        {groups.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', px: 1.25, py: 2 }}>
            No prompts match. You can still ask it in your own words.
          </Typography>
        ) : (
          groups.map((g) => <PromptGroup key={g.category} title={g.category} prompts={g.prompts} onPick={onPick} />)
        )}
      </Box>
    </Stack>
  );
}
